import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Container, Typography, Alert, Avatar } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
    toggleAssetSelection,
    clearError,
    clearAllSelections,
} from '../../../../Redux/cashout/cashoutSlice';
import { CommonDataTable } from '../../SharedComponent/CommonDataTable';
import SelectedAssets from './SelectedAssets';
import { CashoutAsset, CashoutAssetError } from '../../../../types/cashout.types';
import Successpopup from '../../SharedComponent/Successpopup';
import BreadcrumbLink from '../../SharedComponent/BreadcrumbLink';
import { fetchAvailableAssets } from '../../../../Redux/cashout/cashoutThunks';
import { Constant } from '../../../../commom/Constants';
import { usePriceFormatter } from '../../../../hooks';
import { useAppDispatch, useAppSelector } from '../../../../Redux/hooks';

const getCashoutTableColumns = (formatter: any) => [
    {
        name: '',
        selector: (row: CashoutAsset) => row.name,
        width: '24px',
        cell: () => null,
    },

    {
        name: 'Name',
        selector: (row: CashoutAsset) => row.name,
        width: '150px',
        sortable: true,
        cell: (row: CashoutAsset) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Avatar
                    src={row.logo ?? undefined}
                    sx={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(74, 122, 242, 0.2)',
                    }}
                >
                    {row.symbol?.substring(0, 2)?.toUpperCase() || 'CO'}
                </Avatar>

                <Box>
                    <Typography
                        sx={{
                            fontSize: '14px',
                            fontWeight: '800',
                            color: '#FFFFFF',
                            whiteSpace: 'break-spaces',
                        }}
                    >
                        {row.name}
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: '12px',
                            fontWeight: '400',
                            color: 'rgba(255,255,255,0.5)',
                        }}
                    >
                        {row.symbol}
                    </Typography>
                </Box>
            </Box>
        ),
    },

    {
        name: 'Source',
        selector: (row: CashoutAsset) => row.source,
        width: '120px',
        sortable: true,
        cell: (row: CashoutAsset) => (
            <Typography
                variant="caption"
                sx={{
                    backgroundColor: '#4EDF6D33',
                    color: '#4EDF6D',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontWeight: 600,
                    fontSize: '11px',
                }}
            >
                {row.source === 'LOAN' ? 'Loan Asset' : 'User Owned'}
            </Typography>
        ),
    },

    {
        name: 'Price',
        selector: (row: CashoutAsset) => {return formatter.display(row.rate)},
        width: '150px',
        center: true,
        cell: (row: CashoutAsset) => (
            <Typography sx={{ fontSize: '16px', color: '#FFFFFF' }}>
                {formatter.display(row.rate)}
            </Typography>
        ),
    },

    {
        name: 'Available Qty.',
        selector: (row: CashoutAsset) => { return formatter.quantity(row.availableQuantityForCashout).display },
        width: '150px',
        center: true,
        cell: (row: CashoutAsset) => (
            <Typography sx={{ fontSize: '16px', color: '#4A7AF2' }}>
                {formatter.quantity(row.availableQuantityForCashout).display}
            </Typography>
        ),
    },

    {
        name: 'Cash Out',
        selector: (row: CashoutAsset) => { return formatter.balance(row.eligibleCashOut).display },
        width: '150px',
        sortable: true,
        center: true,
        cell: (row: CashoutAsset) => (
            <Typography
                sx={{
                    fontSize: '16px',
                    color: '#4EDF6D',
                    fontWeight: '800',
                }}
            >
                {formatter.balance(row.eligibleCashOut).display}
            </Typography>
        ),
    },
];

interface PopupState {
    open: boolean;
    heading: string;
    message: string;
    success: boolean;
}

const CLOSED_POPUP: PopupState = { open: false, heading: '', message: '', success: false };


export default function CashOut() {
    const navigation = useNavigate();
    const dispatch = useAppDispatch();
    const { availableAssets, selectedAssets, loading, error } = useAppSelector(
        (state) => state.cashout
    );

    const [assetsRefreshing, setAssetsRefreshing] = useState(false);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [popup, setPopup] = useState<PopupState>(CLOSED_POPUP);
    const formatter = usePriceFormatter();
    const CASHOUT_TABLE_COLUMNS =  getCashoutTableColumns(formatter);
    useEffect(() => {
        dispatch(clearAllSelections());
        dispatch(fetchAvailableAssets());
    }, [dispatch]);


    const handleDataTableSelectionChange = (selectedRows: CashoutAsset[]) => {
        const currentSelectedIds = new Set(selectedAssets.map(a => a.id));
        const newSelectedIds = new Set(selectedRows.map(r => r.id));

        currentSelectedIds.forEach(id => {
            if (!newSelectedIds.has(id)) {
                const asset = availableAssets.find(a => a.id === id);
                if (asset) dispatch(toggleAssetSelection(asset));
            }
        });

        newSelectedIds.forEach(id => {
            if (!currentSelectedIds.has(id)) {
                const asset = availableAssets.find(a => a.id === id);
                if (asset) dispatch(toggleAssetSelection(asset));
            }
        });
    };

    const handleCashoutSuccess = (partialFailures?: CashoutAssetError[]) => {
        if (!partialFailures || partialFailures.length === 0) {
            // Full success
            setPopup({
                open: true,
                heading: 'Cash Out Successful',
                message: 'Your selected assets have been successfully cashed out. The funds will reflect in your account shortly.',
                success: true,
            });
        } else {
            // Partial success
            const failedList = partialFailures.map(f => f.asset).join(', ');
            setPopup({
                open: true,
                heading: 'Partially Processed',
                message: `Some assets were cashed out successfully. The following could not be processed: ${failedList}. They have been re-selected for you — please review and try again.`,
                success: true,
            });
        }
    };

    const handleCashoutFailed = (failures: CashoutAssetError[]) => {
        // Build a concise user-friendly message
        const isAllSame = failures.every(f => {
            const msg = f.error.includes(': ') ? f.error.slice(f.error.indexOf(': ') + 2) : f.error;
            return msg === (failures[0].error.includes(': ')
                ? failures[0].error.slice(failures[0].error.indexOf(': ') + 2)
                : failures[0].error);
        });

        let message: string;
        if (failures.length === 1) {
            const humanError = failures[0].error.includes(': ')
                ? failures[0].error.slice(failures[0].error.indexOf(': ') + 2)
                : failures[0].error;
            message = `Could not process ${failures[0].asset}: ${humanError}. Please check your balance or try again later.`;
        } else if (isAllSame) {
            const humanError = failures[0].error.includes(': ')
                ? failures[0].error.slice(failures[0].error.indexOf(': ') + 2)
                : failures[0].error;
            message = `${failures.length} assets could not be processed: ${humanError}. Please review the errors below and try again.`;
        } else {
            message = `${failures.length} assets failed to process. Please review the individual errors in the panel and adjust your selection before retrying.`;
        }

        setPopup({
            open: true,
            heading: 'Cash Out Failed',
            message,
            success: false,
        });
    };

    const handleRefreshAssets = useCallback(async () => {
        setAssetsRefreshing(true);
        // Delay gives backend time to flush DB writes
        await new Promise(res => setTimeout(res, Constant.POST_SUCCESS_REFRESH_DELAY_MS));
        await dispatch(fetchAvailableAssets());
        setAssetsRefreshing(false);
    }, [dispatch]);

    return (
        <Container
            maxWidth={false}
            className="bg-color"
            style={{ minHeight: '100vh', paddingTop: '30px', position: 'relative' }}
        >
            {popup.open && (
                <Successpopup
                    Heading={popup.heading}
                    Success={popup.success}
                    onClose={() => setPopup(CLOSED_POPUP)}
                    Message={popup.message}
                />
            )}

            {/* Breadcrumb */}
            <Typography className="text-secondary" sx={{ marginBottom: { xs: '15px', sm: '20px' } }}>
                <BreadcrumbLink onClick={() => navigation('/boosterLoan')}>Booster Loan</BreadcrumbLink>
                <span style={{ color: '#4A7AF2' }}> / </span>
                <BreadcrumbLink onClick={() => navigation('/boosterLoan')}>Repayments</BreadcrumbLink>
                <span style={{ color: '#4A7AF2' }}> / </span>
                <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>Cash Out</span>
            </Typography>

            {/* Fetch error banner (only for GET /available-assets errors) */}
            {error && (
                <Alert
                    severity="error"
                    sx={{
                        marginBottom: { xs: '15px', sm: '20px' },
                        backgroundColor: 'rgba(255, 107, 107, 0.1)',
                        color: '#FF6B6B',
                        border: '1px solid rgba(255, 107, 107, 0.3)',
                    }}
                    onClose={() => dispatch(clearError())}
                >
                    {error}
                </Alert>
            )}

            {/* Main layout */}
            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: { xs: 'column', lg: 'row' },
                    gap: { xs: 3, lg: 0 },
                    pb: { xs: 3, lg: 0 },
                }}
            >
                {/* Assets Table */}
                <Box
                    sx={{
                        width: { xs: '100%', lg: 'calc(70% - 60px)' },
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: { xs: '15px', sm: '20px' },
                        padding: { xs: '15px', sm: '20px' },
                    }}
                >
                    {assetsRefreshing && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '10px 14px',
                                marginBottom: '12px',
                                backgroundColor: 'rgba(74, 122, 242, 0.08)',
                                border: '1px solid rgba(74, 122, 242, 0.25)',
                                borderRadius: '10px',
                            }}
                        >
                            <CircularProgress size={14} sx={{ color: '#4A7AF2' }} />
                            <Typography sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
                                Refreshing available assets…
                            </Typography>
                        </Box>
                    )}
                    {availableAssets.length > 0 ? (
                        <CommonDataTable
                            data={availableAssets}
                            columns={CASHOUT_TABLE_COLUMNS}
                            totalRows={availableAssets.length}
                            title="Available Assets"
                            setPage={setPage}
                            setRowsPerPage={setRowsPerPage}
                            selectableRows={true}
                            selectableRowSelected={(row: CashoutAsset) =>
                                selectedAssets.some(asset => asset.id === row.id)
                            }
                            onSelectedRowsChange={handleDataTableSelectionChange}
                        />
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: { xs: '250px', sm: '300px' },
                                color: 'rgba(255, 255, 255, 0.5)',
                            }}
                        >
                            {loading ? <CircularProgress /> : 'No assets available'}
                        </Box>
                    )}
                </Box>

                {/* Selected Assets Panel */}
                <SelectedAssets
                    onCashoutSuccess={handleCashoutSuccess}
                    onCashoutFailed={handleCashoutFailed}
                    onRefreshAssets={handleRefreshAssets}
                    refreshing={assetsRefreshing}
                />
            </Box>
        </Container>
    );
}